import CryptoJS from "crypto-js";
import { LIVEKIT_SERVER_URL } from "../../../custom_utilities/baseURL";

const DECRYPT_KEY = "0CiuKpLCZbX7gBCyYTka4USxTnh7ssXKk8bJ6Y3TXPc=";

const SERVER_URL_KEYS = [
  "livekit_url",
  "livekit_server_url",
  "livekit_ws_url",
  "ws_url",
  "server_url",
];

const isJwt = (value) =>
  typeof value === "string" && value.startsWith("eyJ");

const TOKEN_KEYS = [
  "access_token",
  "accessToken",
  "livekit_token",
  "token",
];

const TOKEN_FALLBACK_KEYS = ["join_url", "joinUrl"];

const isLiveKitWebSocketUrl = (value) => {
  const v = String(value).trim();
  return /^wss?:\/\//i.test(v) || /^https?:\/\//i.test(v);
};

/**
 * Decrypt start_url — same AES logic as schedule_class decrypt_functionality.
 * Decrypted value is typically "wss://live.motion.ac.in" (no * separator).
 */
export const decryptStartUrl = (encryptedValue) => {
  if (!encryptedValue) return null;

  const raw = String(encryptedValue).trim();

  if (isLiveKitWebSocketUrl(raw)) return raw;

  try {
    let encrypted = atob(raw);
    encrypted = JSON.parse(encrypted);

    const iv = CryptoJS.enc.Base64.parse(encrypted.iv);
    const key = CryptoJS.enc.Base64.parse(DECRYPT_KEY);

    const decrypted = CryptoJS.AES.decrypt(encrypted.value, key, { iv });
    const text = decrypted.toString(CryptoJS.enc.Utf8).trim();

    if (!text) return null;

    if (text.includes("*")) {
      const valueAfterStar = text.split("*")[1]?.trim();
      if (valueAfterStar) return valueAfterStar;
    }

    return text;
  } catch {
    return null;
  }
};

/** Generic decrypt for other encrypted fields */
export const decryptValue = (value) => {
  if (!value || isJwt(value)) return null;
  const trimmed = String(value).trim();
  if (isLiveKitWebSocketUrl(trimmed)) return trimmed;

  try {
    let encrypted = atob(trimmed);
    encrypted = JSON.parse(encrypted);
    const iv = CryptoJS.enc.Base64.parse(encrypted.iv);
    const key = CryptoJS.enc.Base64.parse(DECRYPT_KEY);
    const decrypted = CryptoJS.AES.decrypt(encrypted.value, key, { iv });
    const text = decrypted.toString(CryptoJS.enc.Utf8).trim();
    if (!text) return null;
    if (text.includes("*")) return text.split("*")[1]?.trim() || text;
    return text;
  } catch {
    return null;
  }
};

const resolveRawValue = (data, keys, { allowJwt = false } = {}) => {
  if (!data) return null;
  for (const key of keys) {
    const raw = data?.[key];
    if (!raw) continue;
    if (allowJwt && isJwt(raw)) return raw.trim();
    const decrypted = decryptValue(raw);
    if (decrypted) return decrypted;
  }
  return null;
};

const isValidLiveKitUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ["http:", "https:", "ws:", "wss:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const isWebPageUrl = (url) => {
  if (!url || isJwt(url)) return false;
  return /^https?:\/\//i.test(url) && !/\/rtc\/v1/i.test(url);
};

/**
 * LiveKit SDK root URL (protocol + host). Accepts wss:// from decrypted start_url.
 */
export const normalizeLiveKitServerUrl = (url) => {
  if (!url || isJwt(url)) return null;

  let normalized = String(url).trim();
  if (!normalized) return null;

  if (!isLiveKitWebSocketUrl(normalized)) {
    if (/^[a-z0-9.-]+(:\d+)?$/i.test(normalized)) {
      normalized = `wss://${normalized}`;
    } else {
      return null;
    }
  }

  try {
    const parsed = new URL(normalized);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return null;
  }
};

export const normalizeLiveKitAccessToken = (token) => {
  if (!token) return null;
  return token.trim().replace(/\s+/g, "");
};

export const normalizeStreamService = (service) =>
  String(service ?? "").trim();

/** stream_service "3" = LiveKit */
export const isLiveKitStream = (data) =>
  normalizeStreamService(data?.stream_service) === "3";

/** Non-recorded live class from schedule / activity — joins via LiveKit only. */
export const isLiveClassJoinable = (data) =>
  Boolean(data) && data?.status_stream !== "recorded" && !data?.rec_id;

/** All live class joins use LiveKit (no Zoom_validation). */
export const shouldJoinViaLiveKit = (data) => isLiveClassJoinable(data);

export const isLiveKitClassLive = (data) => {
  if (!isLiveClassJoinable(data)) return false;
  const status = String(data?.status_stream ?? "").toLowerCase();
  return !status || status === "live";
};

export const prepareLiveClassJoinPayload = (element, redirectedFrom) => ({
  ...element,
  stream_service: "3",
  status_stream: element?.status_stream || "live",
  redirectedFrom,
});

export const getDecryptedStartUrl = (data) => {
  const raw = data?.start_url || data?.startUrl;
  if (!raw) return null;
  return decryptStartUrl(raw);
};

export const getLiveKitEmbedUrl = (data) => {
  for (const key of ["join_url", "joinUrl"]) {
    const raw = data?.[key];
    if (!raw || isJwt(raw)) continue;
    const decrypted = decryptValue(raw);
    if (decrypted && isWebPageUrl(decrypted)) return decrypted;
  }
  return null;
};

const buildServerUrlCandidates = (data) => {
  const candidates = [];
  const seen = new Set();

  const add = (url, source) => {
    const normalized = normalizeLiveKitServerUrl(url);
    if (!normalized || !isValidLiveKitUrl(normalized) || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    candidates.push({ url: normalized, source });
  };

  const startDecrypted = getDecryptedStartUrl(data);
  if (startDecrypted) {
    add(startDecrypted, "start_url_decrypted");
  }

  if (shouldJoinViaLiveKit(data) && candidates.length > 0) {
    return candidates;
  }

  for (const key of SERVER_URL_KEYS) {
    const raw = data?.[key];
    if (!raw || isJwt(raw)) continue;
    const decrypted = decryptValue(raw);
    if (decrypted) add(decrypted, key);
  }

  if (!candidates.length) {
    add(LIVEKIT_SERVER_URL, "default_fallback");
  }

  return candidates;
};

export const getLiveKitServerUrl = (data) => {
  const candidates = buildServerUrlCandidates(data);
  return candidates[0]?.url ?? null;
};

export const getLiveKitServerUrlCandidates = (data) =>
  buildServerUrlCandidates(data).map((c) => c.url);

export const getLiveKitAccessToken = (data) => {
  let token = resolveRawValue(data, TOKEN_KEYS, { allowJwt: true });
  if (!token) {
    for (const key of TOKEN_FALLBACK_KEYS) {
      const value = data?.[key];
      if (isJwt(value)) {
        token = value;
        break;
      }
    }
  }
  return normalizeLiveKitAccessToken(token);
};

export const hasLiveKitCredentials = (data) => {
  if (getLiveKitEmbedUrl(data)) return true;
  return Boolean(getLiveKitAccessToken(data) && getLiveKitServerUrl(data));
};

export const getLiveKitConnectionConfig = (data) => {
  const embedUrl = getLiveKitEmbedUrl(data);
  if (embedUrl) {
    return { mode: "embed", embedUrl };
  }

  const token = getLiveKitAccessToken(data);
  const decryptedStartUrl = getDecryptedStartUrl(data);
  const serverUrlCandidates = getLiveKitServerUrlCandidates(data);

  if (!token || !serverUrlCandidates.length) return null;

  return {
    mode: "sdk",
    serverUrl: serverUrlCandidates[0],
    token,
    serverUrlCandidates,
    decryptedStartUrl,
  };
};

export const getLiveKitJoinError = (data) => {
  if (!isLiveClassJoinable(data)) {
    if (data?.status_stream === "recorded" || data?.rec_id) {
      return "This class is recorded. Use Watch Now instead.";
    }
    return null;
  }
  if (getLiveKitEmbedUrl(data)) return null;
  if (!getLiveKitAccessToken(data)) {
    return "LiveKit access token is missing. Please refresh and try again.";
  }
  return null;
};

export const LIVEKIT_JOIN_BASE =
  "https://learninguat.motion.ac.in/motioneducation/livekit/join";

export const getLiveKitJoinUrl = (meetingId, userId = "") => {
  const id = meetingId || "unknown";
  const params = new URLSearchParams();
  if (userId) params.set("user_id", String(userId));
  const query = params.toString();
  return `${LIVEKIT_JOIN_BASE}/${id}${query ? `?${query}` : ""}`;
};

/** Same-tab navigation — LiveKit host blocks cross-origin iframe embed. */
export const openLiveKitJoin = (meetingId, userId = "") => {
  window.location.assign(getLiveKitJoinUrl(meetingId, userId));
};
